/**
 * Ends the session on this device once the server has already stopped honouring it: an
 * authenticated call came back 401 (the token was revoked by a password change, a deactivation,
 * or a sign-out elsewhere), or the account itself was deleted.
 *
 * In both cases no authenticated request can succeed any more, so everything here is local-only
 * or best-effort, and the last step is a full document load of the login page. Not `navigateTo`:
 * a full load guarantees every store, cached session and in-flight request is gone, whereas a soft
 * navigation can leave a composable holding the old user and re-fire the same 401 on the login page.
 *
 * The composable must be created inside a Nuxt context (a component, a plugin, or
 * `nuxtApp.runWithContext`); `tearDownSession` itself can then run from anywhere, including an
 * ofetch hook, because everything it needs is resolved up front.
 */
export type SessionTeardownOptions = {
  /** Where the full document load lands. */
  redirectTo?: string;
  /**
   * Also discard unsent offline lead scans. Only right when the account is gone for good: a token
   * that merely died must keep them, they belong to the person who will log back in.
   */
  dropOfflineLeads?: boolean;
};

const FCM_TEARDOWN_TIMEOUT_MS = 2000;

export const useSessionTeardown = () => {
  const notificationsStore = useNotificationsStore();
  const offlineLeadQueueStore = useOfflineLeadQueueStore();
  const badgeStore = useBadgeStore();
  const { optOutFromAll } = useFirebaseNotification();
  const tokenCookie = useCookie('wad-auth._token.local');

  const tearDownSession = async ({ redirectTo = '/user/login', dropOfflineLeads = false }: SessionTeardownOptions = {}) => {
    try {
      // Drop the device's Firebase token. `false` skips the API opt-out call, which would 401 for
      // the same reason; this only talks to FCM and the service worker. FCM can hang on a slow
      // network, so it is capped rather than awaited to completion.
      await Promise.race([
        optOutFromAll(false).catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, FCM_TEARDOWN_TIMEOUT_MS)),
      ]);
      notificationsStore.clearFcmToken();
      if (dropOfflineLeads) {
        offlineLeadQueueStore.clearAll();
      }
      badgeStore.clear();
      if (import.meta.client && 'caches' in window) {
        await Promise.all([
          caches.delete('session-cache'),
          caches.delete('event-api-cache'),
        ]).catch(() => {});
      }
      // Clearing the cookie is what actually ends the session for nuxt-auth.
      tokenCookie.value = null;
    } finally {
      if (import.meta.client) {
        window.location.href = redirectTo;
      }
    }
  };

  return { tearDownSession };
};
